import { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Image,
} from "react-bootstrap";
import { update } from "../../api/API";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { FaEdit } from "react-icons/fa";
import {
  getCurrentUser,
  isLoggedIn,
  getToken,
  updateUser,
} from "../../utility/UserUtils";

const API_BASE = process.env.REACT_APP_API_URL;

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    avatar: "",
    dob: "",
    experiences: [{ jobTitle: "", companyName: "", location: "", years: 0 }],
    location: "",
    skills: [],
    linkedin: "",
    website: "",
    bio: "",
    socialLinks: { twitter: "", facebook: "", instagram: "" },
    cohort: null,
    canGiveReferral: true,
    address: { street: "", city: "", state: "", zip: "", country: "" },
  });
  const [cohorts, setCohorts] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // ✅ Load user from utility
  useEffect(() => {
    if (isLoggedIn()) {
      const currentUser = getCurrentUser();
      setUser(currentUser);
      resetForm(currentUser);
    }
  }, []);

  // ✅ Fetch cohorts
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/cohorts?limit=100`);
        setCohorts(res.data.docs);
      } catch (err) {
        console.error("Failed to fetch cohorts:", err);
      }
    };
    fetchCohorts();
  }, []);

  const resetForm = (userData) => {
    setForm({
      firstName: userData.firstName || userData.name || "",
      lastName: userData.lastName || "",
      email: userData.email || "",
      phone: userData.phone || "",
      avatar: userData.avatar || "",
      dob: userData.dob
        ? new Date(userData.dob).toISOString().split("T")[0]
        : "",
      address: userData.address || {
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "",
      },
      experiences:
        userData.experiences && userData.experiences.length > 0
          ? userData.experiences
          : [{ jobTitle: "", companyName: "", location: "", years: 0 }],
      location: userData.location || "",
      skills: userData.skills?.map((s) => s.skill) || [],
      linkedin: userData.linkedin || "",
      website: userData.website || "",
      bio: userData.bio || "",
      socialLinks: userData.socialLinks || {
        twitter: "",
        facebook: "",
        instagram: "",
      },
      cohort: userData.cohort
        ? {
            id: userData.cohort.id,
            title: userData.cohort.title,
            range: userData.cohort.range,
          }
        : null,
      canGiveReferral: userData.canGiveReferral ?? true,
    });
  };

  //Profile photo upload using token from utility
  const uploadProfilePhoto = async (file) => {
    const token = getToken();
    if (!token) {
      console.error("No token found — user not authenticated");
      return;
    }

    const formData = new FormData();
    formData.append(
      "_payload",
      JSON.stringify({ alt: file.name, prefix: "profile-photos" })
    );
    formData.append("file", file);

    try {
      const res = await axios.post(`${API_BASE}/profile-photos`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      console.error("[uploadProfilePhoto] Upload failed", err);
      throw err;
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const validateForm = () => {
    const newErrors = {};
    const safeTrim = (value) => (value ? value.trim() : "");

    if (!safeTrim(form.firstName))
      newErrors.firstName = "First name is required.";
    if (!safeTrim(form.lastName)) newErrors.lastName = "Last name is required.";
    if (!safeTrim(form.phone)) newErrors.phone = "Phone number is required.";
    if (safeTrim(form.phone) && !/^[0-9+\-\s()]+$/.test(form.phone))
      newErrors.phone = "Invalid phone number.";
    if (!form.dob) newErrors.dob = "Date of birth is required.";
    // if (!safeTrim(form.address?.street))
    //   newErrors.street = "Street is required.";
    if (!safeTrim(form.address?.city)) newErrors.city = "City is required.";
    // if (!safeTrim(form.address?.state)) newErrors.state = "State is required.";
    if (!safeTrim(form.address?.zip)) newErrors.zip = "ZIP code is required.";
    // if (!safeTrim(form.address?.country))
    //   newErrors.country = "Country is required.";
    if (!form.cohort?.id) newErrors.cohort = "Please select a cohort.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //Save profile and update user utility + auth event
  const handleSave = async () => {
    if (!user) {
      return;
    }

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      let avatarId = user.avatar?.id || user.avatar;
      if (file) {
        const uploadRes = await uploadProfilePhoto(file);
        avatarId = uploadRes.doc.id;
      }

      const payloadSkills = form.skills.map((skill) => ({ skill }));

      const formData = new FormData();
      formData.append(
        "_payload",
        JSON.stringify({
          ...form,
          dob: form.dob ? new Date(form.dob).toISOString() : null,
          avatar: avatarId,
          skills: payloadSkills,
          cohort: form.cohort?.id || null,
          address: form.address,
        })
      );

      const token = getToken();

      const res = await update(`/members/${user.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const updatedUser = res.doc;
      updateUser(updatedUser);
      setUser(updatedUser);
      resetForm(updatedUser);

      window.dispatchEvent(new Event("authChange"));

      setFile(null);
      setPreview(null);
      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) resetForm(user);
    setFile(null);
    setPreview(null);
    setIsEditing(false);
  };

  const getAvatarUrl = () => {
    if (preview) return preview;
    if (user?.avatar?.url) return user.avatar.url;
    if (typeof user?.avatar === "string")
      return `${API_BASE}/media/${user.avatar}`;
    return "/assets/images/default-avatar.png";
  };

  const addSkill = () => setForm({ ...form, skills: [...form.skills, ""] });
  const updateSkill = (idx, value) => {
    const updated = [...form.skills];
    updated[idx] = value;
    setForm({ ...form, skills: updated });
  };

  const addExperience = () =>
    setForm({
      ...form,
      experiences: [
        ...form.experiences,
        { jobTitle: "", companyName: "", location: "", years: 0 },
      ],
    });

  const updateExperience = (idx, field, value) => {
    const updated = [...form.experiences];
    updated[idx][field] = value;
    setForm({ ...form, experiences: updated });
  };

  const removeExperience = (idx) => {
    const updated = form.experiences.filter((_, i) => i !== idx);
    setForm({ ...form, experiences: updated });
  };

  if (!user) return <p className="text-center mt-5">Loading profile...</p>;

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <LoadingSpinner text="Updating Profile..." />
        </div>
      )}
      <Container className={`py-4 ${loading ? "page-blur" : ""}`}>
        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h1 className="mb-0">Profile</h1>
            {!isEditing && (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                Edit <FaEdit className="ml-2" />
              </Button>
            )}
          </Card.Header>
          <Card.Body>
            {/* Avatar */}
            <div className="d-flex align-items-center mb-4">
              <Image
                src={getAvatarUrl()}
                roundedCircle
                width={120}
                height={120}
                className="me-3 border"
              />
              {isEditing && (
                <Form.Group>
                  <Form.Label>Change Photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Form.Group>
              )}
            </div>

            {/* Personal Info */}
            <h5 className="border-bottom pb-2">Personal Info</h5>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.firstName}
                    readOnly={!isEditing}
                    isInvalid={!!errors.firstName}
                    onChange={(e) =>
                      setForm({ ...form, firstName: e.target.value })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.lastName}
                    readOnly={!isEditing}
                    isInvalid={!!errors.lastName}
                    onChange={(e) =>
                      setForm({ ...form, lastName: e.target.value })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Email / Phone */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={form.email} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.phone}
                    readOnly={!isEditing}
                    isInvalid={!!errors.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Date of Birth */}
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth *</Form.Label>
              <Form.Control
                type="date"
                value={form.dob}
                readOnly={!isEditing}
                isInvalid={!!errors.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
              />
              <Form.Control.Feedback type="invalid">
                {errors.dob}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Address */}
            <h5 className="border-bottom pb-2 mt-4">Address</h5>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Street</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address.street}
                    readOnly={!isEditing}
                    isInvalid={!!errors.street}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        address: { ...form.address, street: e.target.value },
                      })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.street}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    City <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address.city}
                    readOnly={!isEditing}
                    isInvalid={!!errors.city}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        address: { ...form.address, city: e.target.value },
                      })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address.state}
                    readOnly={!isEditing}
                    isInvalid={!!errors.state}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        address: { ...form.address, state: e.target.value },
                      })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.state}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    ZIP Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address.zip}
                    readOnly={!isEditing}
                    isInvalid={!!errors.zip}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        address: { ...form.address, zip: e.target.value },
                      })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.zip}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address.country}
                    readOnly={!isEditing}
                    isInvalid={!!errors.country}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        address: { ...form.address, country: e.target.value },
                      })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.country}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Cohort */}
            <h5 className="border-bottom pb-2 mt-4">
              Cohort<span className="text-danger">*</span>
            </h5>
            {!isEditing ? (
              <Form.Control
                type="text"
                value={
                  form.cohort
                    ? `${form.cohort.title} – ${form.cohort.range}`
                    : ""
                }
                readOnly
              />
            ) : (
              <>
                <Form.Select
                  value={form.cohort?.id || ""}
                  isInvalid={!!errors.cohort}
                  onChange={(e) => {
                    const selected = cohorts.find(
                      (c) => c.id === e.target.value
                    );
                    setForm({
                      ...form,
                      cohort: selected
                        ? {
                            id: selected.id,
                            title: selected.title,
                            range: selected.range,
                          }
                        : null,
                    });
                  }}
                >
                  <option value="">Select your cohort</option>
                  {cohorts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title} – {c.range}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.cohort}
                </Form.Control.Feedback>
                <Form.Check
                  type="checkbox"
                  label="I can give referrals"
                  checked={form.canGiveReferral}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setForm({ ...form, canGiveReferral: e.target.checked })
                  }
                />
              </>
            )}

            {/* Professional Info */}
            <h5 className="border-bottom pb-2 mt-4">Professional Info</h5>
            {form.experiences.map((exp, idx) => (
              <div key={idx} className="border rounded p-3 mb-3">
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Job Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={exp.jobTitle}
                        readOnly={!isEditing}
                        onChange={(e) =>
                          updateExperience(idx, "jobTitle", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Company</Form.Label>
                      <Form.Control
                        type="text"
                        value={exp.companyName}
                        readOnly={!isEditing}
                        onChange={(e) =>
                          updateExperience(idx, "companyName", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        type="text"
                        value={exp.location}
                        readOnly={!isEditing}
                        onChange={(e) =>
                          updateExperience(idx, "location", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Experience (Years)</Form.Label>
                      <Form.Control
                        type="number"
                        value={exp.years}
                        readOnly={!isEditing}
                        onChange={(e) =>
                          updateExperience(idx, "years", Number(e.target.value))
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {isEditing && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeExperience(idx)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            {isEditing && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addExperience}
              >
                + Add Experience
              </Button>
            )}

            {/* LinkedIn, Website, Bio, Skills, Social Links remain unchanged */}
            <Form.Group className="mb-3 mt-4">
              <Form.Label>LinkedIn</Form.Label>
              <Form.Control
                type="text"
                value={form.linkedin}
                readOnly={!isEditing}
                onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Website</Form.Label>
              <Form.Control
                type="text"
                value={form.website}
                readOnly={!isEditing}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Bio</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.bio}
                readOnly={!isEditing}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
              />
            </Form.Group>

            {/* Skills */}
            <h5 className="border-bottom pb-2 mt-4">Skills</h5>
            {form.skills.map((skill, idx) => (
              <Form.Control
                key={idx}
                type="text"
                className="mb-2"
                value={skill}
                readOnly={!isEditing}
                onChange={(e) => updateSkill(idx, e.target.value)}
              />
            ))}
            {isEditing && (
              <Button variant="outline-primary" size="sm" onClick={addSkill}>
                + Add Skill
              </Button>
            )}

            {/* Social Links */}
            <h5 className="border-bottom pb-2 mt-4">Social Links</h5>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Twitter</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.socialLinks.twitter}
                    readOnly={!isEditing}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        socialLinks: {
                          ...form.socialLinks,
                          twitter: e.target.value,
                        },
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Facebook</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.socialLinks.facebook}
                    readOnly={!isEditing}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        socialLinks: {
                          ...form.socialLinks,
                          facebook: e.target.value,
                        },
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Instagram</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.socialLinks.instagram}
                    readOnly={!isEditing}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        socialLinks: {
                          ...form.socialLinks,
                          instagram: e.target.value,
                        },
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Save / Cancel */}
            {isEditing && (
              <div className="d-flex gap-2 mt-4">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={loading}
                >
                  Save
                </Button>
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ProfilePage;
